// Curated emoji icon picker — compact picker-field in forms, full search +
// category-chip + grid surface on demand (picker-sheet.js).
//
// Public API:
//
//   iconPicker(initialValue?: string, palette?: PaletteGroup[]):
//     { el, getValue(), setValue(v) }
//
// Palette shape (matches /api/v1/icons/palette):
//   [{ key: string, emojis: [{ char, label_key, alias_keys?: string[] }] }]
//
// Behaviors:
//   - `el` is a compact `.picker-field` row (current emoji + localized label
//     + "Change ›"); the full selection UI is NOT inline in the form.
//   - Activating `el` opens the picker surface via pickerSheet: search,
//     horizontally scrollable category chips (Recents first when non-empty,
//     then All, then palette groups — one pattern at every viewport), and a
//     responsive emoji grid.
//   - Search matches localized label + aliases, ranking starts-with matches
//     before contains matches.
//   - Recents (last 8 picks) persist via localStorage.
//   - Free-text emoji entry lives behind a "＋ own" tile at the end of the
//     grid; entering a value there is accepted verbatim and takes effect
//     immediately as the selection.
//   - Selecting an emoji (grid or free-text) updates the picker field,
//     dispatches a bubbling `change` event on it, and closes the surface.

import { apiGet } from '../api.js';
import { t } from '../i18n.js';
import { pickerSheet } from './picker-sheet.js';
import { chevronDown } from './icons.js';

const RECENTS_KEY = 'lardr.iconpicker.recents';
const RECENTS_MAX = 8;

/** @type {Array<{key: string, emojis: Array<{char: string, label_key: string, alias_keys?: string[]}>}>|null} */
let cachedPalette = null;

export async function fetchPalette() {
  if (cachedPalette !== null) return cachedPalette;
  try {
    const data = await apiGet('/icons/palette');
    cachedPalette = Array.isArray(data) ? data : [];
  } catch (_) {
    cachedPalette = [];
  }
  return cachedPalette;
}

function readRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter(s => typeof s === 'string') : [];
  } catch (_) {
    return [];
  }
}

function writeRecents(list) {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list));
  } catch (_) {
    // private mode / quota — silent degrade
  }
}

function pushRecent(emoji) {
  const cur = readRecents().filter(e => e !== emoji);
  cur.unshift(emoji);
  if (cur.length > RECENTS_MAX) cur.length = RECENTS_MAX;
  writeRecents(cur);
  return cur;
}

function normalizeForSearch(s) {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Build a flat searchable list from the palette, deduped by char. */
function flattenPalette(groups) {
  const seen = new Set();
  const out = [];
  for (const g of groups) {
    for (const e of g.emojis) {
      if (seen.has(e.char)) continue;
      seen.add(e.char);
      out.push(e);
    }
  }
  return out;
}

/** Resolve search terms for one entry, localized. */
function searchTermsFor(entry) {
  const terms = [t(entry.label_key)];
  if (Array.isArray(entry.alias_keys)) {
    for (const k of entry.alias_keys) terms.push(t(k));
  }
  return terms.map(normalizeForSearch);
}

/**
 * @param {string|null} [initialValue]
 * @param {Array<{key: string, emojis: Array<{char: string, label_key: string, alias_keys?: string[]}>}>} [palette]
 */
export function iconPicker(initialValue = null, palette = null) {
  const groups = palette && palette.length > 0 ? palette : (cachedPalette || []);
  const flat = flattenPalette(groups);

  let value = initialValue || null;
  let recents = readRecents();

  function matchedGroupKeyFor(v) {
    if (!v) return null;
    for (const g of groups) {
      if (g.emojis.some(e => e.char === v)) return g.key;
    }
    return null;
  }

  function defaultChip() {
    const matched = matchedGroupKeyFor(value);
    if (matched) return matched;
    if (recents.length > 0) return '__recents__';
    return groups[0]?.key ?? '__all__';
  }

  // --- Compact picker field (the only part rendered inline in the form) ---

  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'picker-field';
  el.dataset.role = 'icon-picker-field';

  const leftWrap = document.createElement('span');
  leftWrap.className = 'flex items-center gap-2 min-w-0';
  const emojiSpan = document.createElement('span');
  emojiSpan.className = 'text-2xl leading-none shrink-0';
  emojiSpan.setAttribute('aria-hidden', 'true');
  const labelSpan = document.createElement('span');
  labelSpan.className = 'text-sm font-medium truncate';
  leftWrap.append(emojiSpan, labelSpan);

  const hint = document.createElement('span');
  hint.className = 'picker-field__hint inline-flex items-center gap-0.5';
  const hintText = document.createElement('span');
  hintText.dataset.role = 'icon-picker-hint-text';
  hint.append(hintText, chevronDown('-rotate-90 w-3.5 h-3.5 opacity-70'));

  el.append(leftWrap, hint);

  function renderField() {
    hintText.textContent = t('icons.change');
    if (value) {
      emojiSpan.hidden = false;
      emojiSpan.textContent = value;
      const found = flat.find(e => e.char === value);
      labelSpan.textContent = found ? t(found.label_key) : t('icons.custom_label');
      labelSpan.classList.remove('text-ink-muted');
    } else {
      emojiSpan.hidden = true;
      emojiSpan.textContent = '';
      labelSpan.textContent = t('icons.none_selected');
      labelSpan.classList.add('text-ink-muted');
    }
  }

  // --- Picker surface (search + category chips + grid), built fresh on
  //     every activation so it always reflects the current value/recents. ---

  function buildSurface({ close }) {
    let activeChip = defaultChip();
    let searchQuery = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'space-y-3';

    const searchInp = document.createElement('input');
    searchInp.type = 'search';
    searchInp.className = 'input';
    searchInp.placeholder = t('icons.search_placeholder');
    searchInp.setAttribute('aria-label', t('icons.search_placeholder'));

    const chipRow = document.createElement('div');
    chipRow.className = 'flex gap-2 overflow-x-auto pb-1 no-scrollbar';
    chipRow.dataset.role = 'icon-picker-chip-row';

    const grid = document.createElement('div');
    grid.className = 'grid grid-cols-5 lg:grid-cols-8 xl:grid-cols-10 gap-2';
    grid.dataset.role = 'icon-picker-grid';

    const noResults = document.createElement('div');
    noResults.className = 'text-sm text-ink-muted';
    noResults.textContent = t('icons.no_results');
    noResults.hidden = true;

    function chipKeys() {
      const keys = [];
      if (recents.length > 0) keys.push('__recents__');
      keys.push('__all__');
      for (const g of groups) keys.push(g.key);
      return keys;
    }

    function chipLabel(key) {
      if (key === '__recents__') return t('icons.category.recents');
      if (key === '__all__') return t('icons.category.all');
      return t(`icons.category.${key}`);
    }

    function styleChip(btn, active) {
      btn.className = active
        ? 'shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-medium bg-primary-600 dark:bg-primary-500 text-white'
        // `.hover-glow` (app.css) rather than a new palette step — no
        // `chip-neutral` hover shade is declared, and this utility already
        // exists for exactly "soft tint on an interactive row".
        : 'shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-medium bg-chip-neutral dark:bg-chip-neutral-dark text-chip-neutral-ink dark:text-chip-neutral-ink-dark hover-glow';
    }

    function renderChips() {
      while (chipRow.firstChild) chipRow.removeChild(chipRow.firstChild);
      for (const key of chipKeys()) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = chipLabel(key);
        btn.dataset.chip = key;
        btn.dataset.role = 'icon-picker-chip';
        styleChip(btn, key === activeChip && searchQuery.length === 0);
        btn.addEventListener('click', () => {
          activeChip = key;
          searchQuery = '';
          searchInp.value = '';
          renderChips();
          renderGrid();
        });
        chipRow.append(btn);
      }
    }

    function entriesForChip() {
      if (activeChip === '__recents__') {
        return recents.map(char => flat.find(e => e.char === char) ?? { char, label_key: '__free__' });
      }
      if (activeChip === '__all__') return flat;
      const g = groups.find(g => g.key === activeChip);
      return g ? g.emojis : [];
    }

    function entriesForSearch() {
      const q = normalizeForSearch(searchQuery);
      const starts = [];
      const includes = [];
      for (const e of flat) {
        const terms = searchTermsFor(e);
        if (terms.some(term => term.startsWith(q))) {
          starts.push(e);
        } else if (terms.some(term => term.includes(q))) {
          includes.push(e);
        }
      }
      return [...starts, ...includes];
    }

    function styleGridBtn(btn, isActive) {
      btn.classList.toggle('border-primary-500', isActive);
      btn.classList.toggle('bg-primary-500/10', isActive);
      btn.classList.toggle('border-transparent', !isActive);
    }

    function commitSelection(char, { trackRecent = true } = {}) {
      value = char;
      if (trackRecent) recents = pushRecent(char);
      renderField();
      el.dispatchEvent(new Event('change', { bubbles: true }));
      close();
    }

    function makeCell(entry) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'flex items-center justify-center rounded-lg border-2 p-2 text-center transition-colors min-h-touch';
      btn.dataset.emoji = entry.char;
      const label = entry.label_key === '__free__' ? entry.char : t(entry.label_key);
      btn.title = label;
      btn.setAttribute('aria-label', label);

      const span = document.createElement('span');
      span.className = 'text-2xl leading-none';
      span.textContent = entry.char;
      btn.append(span);

      btn.addEventListener('click', () => commitSelection(entry.char));
      styleGridBtn(btn, entry.char === value);
      return btn;
    }

    function makeOwnTile() {
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'flex items-center justify-center rounded-lg border-2 border-dashed border-border dark:border-border-dark p-2 text-center text-sm text-ink-muted transition-colors min-h-touch hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400';
      tile.dataset.role = 'icon-picker-own-tile';
      tile.textContent = t('icons.own_tile');
      tile.title = t('icons.free_text_label');
      tile.setAttribute('aria-label', t('icons.free_text_label'));

      tile.addEventListener('click', () => {
        const inputCell = document.createElement('div');
        inputCell.className = 'col-span-2 flex items-center';
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'input py-1 text-lg';
        input.maxLength = 16;
        input.placeholder = '🐢';
        input.dataset.role = 'icon-picker-free-input';
        input.setAttribute('aria-label', t('icons.free_text_label'));
        inputCell.append(input);
        tile.replaceWith(inputCell);
        input.focus();

        function commit() {
          const v = input.value.trim();
          if (v === '') return;
          commitSelection(v, { trackRecent: false });
        }
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        });
        input.addEventListener('change', commit);
      });

      return tile;
    }

    function renderGrid() {
      while (grid.firstChild) grid.removeChild(grid.firstChild);
      const entries = searchQuery.length > 0 ? entriesForSearch() : entriesForChip();
      noResults.hidden = entries.length > 0;
      for (const entry of entries) {
        grid.append(makeCell(entry));
      }
      grid.append(makeOwnTile());
    }

    searchInp.addEventListener('input', () => {
      searchQuery = searchInp.value.trim();
      for (const b of chipRow.querySelectorAll('button')) {
        styleChip(b, b.dataset.chip === activeChip && searchQuery.length === 0);
      }
      renderGrid();
    });

    wrapper.append(searchInp, chipRow, grid, noResults);
    renderChips();
    renderGrid();
    return wrapper;
  }

  el.addEventListener('click', () => {
    pickerSheet({
      trigger: el,
      title: t('icons.picker_title'),
      content: ({ close }) => buildSurface({ close }),
    });
  });

  renderField();

  return {
    el,
    getValue() {
      return value;
    },
    setValue(v) {
      value = v || null;
      renderField();
      el.dispatchEvent(new Event('change', { bubbles: true }));
    },
  };
}
